/**
 * User-agent detection helpers — primarily to recognise embedded webviews
 * where Google OAuth is blocked by Google's "Use secure browsers" policy
 * (error: disallowed_useragent).
 *
 * Google blocks OAuth flows in any user-agent it considers an embedded
 * webview, including in-app browsers in Facebook, Instagram, LinkedIn,
 * WhatsApp, Line, WeChat, TikTok, Snapchat, Slack, and others. The user
 * is required to open the link in a "real" browser (Safari / Chrome /
 * Firefox / Edge / etc.) for OAuth to succeed.
 *
 * Source: https://developers.google.com/identity/protocols/oauth2/policies#browser-restrictions
 */

/**
 * Returns true if the current user-agent looks like an embedded webview
 * where Google OAuth will fail. Use to gate Google sign-in UI and show
 * a helpful "open in your browser" message instead of letting the user
 * tap Google sign-in only to be bounced by Google's policy block.
 *
 * Conservative: false-positive rate is low because we only flag known
 * patterns. Bare-Safari iOS, Chrome Android, Firefox, Edge, and proper
 * PWA Trusted Web Activities are NOT flagged.
 */
export function isInAppBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';

  // Known social / messaging in-app browsers by UA token.
  const inAppPatterns = [
    /FBAN|FBAV/,                  // Facebook
    /Instagram/,                  // Instagram
    /LinkedInApp/,                // LinkedIn
    /Twitter/,                    // Twitter (older app builds)
    /Line\//,                     // Line
    /MicroMessenger/,             // WeChat
    /BytedanceWebview|musical_ly/,// TikTok
    /Snapchat/,                   // Snapchat
    /Slack/,                      // Slack
    /Pinterest/,                  // Pinterest
  ];
  if (inAppPatterns.some((p) => p.test(ua))) return true;

  // Android WebView (NOT Chrome Custom Tabs / TWA) — the "; wv)" token
  // identifies a raw WebView. Trusted Web Activities use Chrome and
  // do NOT carry this token, so installed PWAs aren't flagged.
  if (/Android.+;\s+wv\)/.test(ua)) return true;

  // iOS WKWebView used in-app — distinguished from Safari / Chrome iOS /
  // Firefox iOS / Edge iOS by absence of any browser-vendor token. iOS
  // Safari has "Safari/", Chrome has "CriOS", Firefox has "FxiOS",
  // Edge has "EdgiOS", Google Search has "GSA/". WKWebView in another
  // app's chrome will have none of these.
  if (/iPhone|iPad|iPod/.test(ua)) {
    const hasBrowserToken = /Safari\/|CriOS|FxiOS|EdgiOS|GSA\//.test(ua);
    if (!hasBrowserToken) return true;
  }

  return false;
}

/** Returns true on Android. Used to pick the right webview-escape pattern
 *  (Android supports `intent://` URLs that reliably hop to Chrome). */
export function isAndroid(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/.test(navigator.userAgent || '');
}

/** Returns true on iOS (iPhone/iPad/iPod). Used to show iOS-specific
 *  escape instructions (no programmatic webview escape exists on iOS). */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent || '');
}

/** Build an Android intent:// URL that asks the OS to open `url` in
 *  Chrome. Falls back to default browser if Chrome isn't installed
 *  (S.browser_fallback_url). Reliably escapes most Android in-app
 *  browsers (Facebook, Instagram, WhatsApp, etc.). */
export function buildAndroidChromeIntent(url: string): string {
  const stripped = url.replace(/^https?:\/\//, '');
  return `intent://${stripped}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end`;
}
